/**
 * useViewerPortfolio — viewer-scoped portfolio hook.
 *
 * WO3 user-data-isolation contract: NEVER surfaces internal Adversyn paper
 * accounts (RAM / Agro / sim-* / OANDA -001/-002). Calls the WO2 backend
 * endpoint GET /api/v1/user/portfolio which derives the owner from the
 * Bearer session and filters internal IDs server-side.
 *
 * Empty state when source === 'none':
 *   No paper trading account connected yet. Adversyn does not yet support
 *   live broker linking — your activity will live in Simulation Lab.
 */
import { useEffect, useState } from 'react';
import { apiV1Base } from '../lib/apiConfig';

const TOKEN_KEY = 'adversyn_auth_token';

// WO12 PortfolioSource adds alpaca_paper_self_connected (2026-06-06)
// Backend get_user_portfolio returns 'alpaca_paper_self_connected' for
// users with a user_broker_connections row. The Trading UI was missing
// this value from the union so any code branching on source treated it
// as unknown/none.
export type PortfolioSource = 'none' | 'simulation_lab' | 'connected_broker' | 'alpaca_paper_self_connected';

// 2026-06-06 alpaca-paper-real-account-sync — closed vocab matching the
// backend `alpaca_paper_sync` SYNC_STATUS_* constants.
export type SyncStatus = 'not_connected' | 'sync_pending' | 'synced' | 'sync_failed';

export interface ViewerAccountSnapshot {
  equity?: string | null;
  last_equity?: string | null;
  cash?: string | null;
  buying_power?: string | null;
  portfolio_value?: string | null;
  long_market_value?: string | null;
  short_market_value?: string | null;
  initial_margin?: string | null;
  maintenance_margin?: string | null;
  daytrade_buying_power?: string | null;
  pattern_day_trader?: boolean;
  trading_blocked?: boolean;
  account_blocked?: boolean;
  transfers_blocked?: boolean;
  currency?: string | null;
  account_number_masked?: string | null;
}

export interface ViewerPosition {
  symbol?: string | null;
  qty?: string | null;
  side?: string | null;
  market_value?: string | null;
  avg_entry_price?: string | null;
  unrealized_pl?: string | null;
  unrealized_plpc?: string | null;
  current_price?: string | null;
  lastday_price?: string | null;
  change_today?: string | null;
}

export interface ViewerOpenOrder {
  id_masked?: string | null;
  symbol?: string | null;
  qty?: string | null;
  side?: string | null;
  type?: string | null;
  status?: string | null;
  limit_price?: string | null;
  stop_price?: string | null;
  submitted_at?: string | null;
}

export interface ViewerPortfolio {
  user_id: string | null;
  source: PortfolioSource;
  connections: Array<{
    connection_id?: string;
    provider_label?: string | null;
    status?: string | null;
    mode?: string | null;
    validation_status?: string | null;
    live_trading?: number | boolean | null;
    key_last4?: string | null;
    created_at_utc?: string | null;
  }>;
  paper_accounts: Array<{
    paper_account_id?: string;
    account_external_id_redacted?: string | null;
    status?: string | null;
  }>;
  paper_account_connected: boolean;
  buying_power: number | null;
  cash: number | null;
  equity: number | null;
  portfolio_value: number | null;
  empty_state_copy: string | null;
  account_label?: string | null;
  base_currency?: string | null;
  // 2026-06-06 alpaca-paper-real-account-sync — additive fields the
  // backend `/api/v1/user/portfolio` route now emits when the viewer has
  // a connected Alpaca paper broker row. All optional so older callers
  // continue to compile.
  sync_status?: SyncStatus | null;
  last_sync_at?: string | null;
  last_success_at?: string | null;
  sync_error_code?: string | null;
  broker_connection_summary?: {
    connection_id?: string | null;
    provider_label?: string | null;
    mode?: string | null;
    status?: string | null;
    validation_status?: string | null;
    display_name?: string | null;
    trade_permission?: boolean;
    live_trading?: boolean;
  } | null;
  account?: ViewerAccountSnapshot | null;
  positions?: ViewerPosition[];
  open_orders?: ViewerOpenOrder[];
  positions_unavailable_pending_sync?: boolean;
}

const EMPTY: ViewerPortfolio = {
  user_id: null,
  source: 'none',
  connections: [],
  paper_accounts: [],
  paper_account_connected: false,
  buying_power: null,
  cash: null,
  equity: null,
  portfolio_value: null,
  empty_state_copy:
    'No paper trading account connected yet. ' +
    'Adversyn does not yet support live broker linking — ' +
    'your activity will live in Simulation Lab.',
  sync_status: 'not_connected',
  last_sync_at: null,
  last_success_at: null,
  sync_error_code: null,
  broker_connection_summary: null,
  account: null,
  positions: [],
  open_orders: [],
  positions_unavailable_pending_sync: false,
};

interface FetchState {
  data: ViewerPortfolio;
  loading: boolean;
  error: string | null;
  authenticated: boolean;
  // WO10 add refetch (2026-06-06) — exposed so the Alpaca self-connect
  // form (and any future flow that mutates broker connections) can
  // trigger a fresh /api/v1/user/portfolio fetch without a full reload.
  refetch?: () => void;
}

// PULSEPERF-002 FIX 5: module-level last-good cache so the shell's portfolio
// fetch is cache-first. A tab-switch remount renders the last-known portfolio
// instantly (no loading flash) and refreshes in the background; a transient
// network error keeps the cached data instead of blanking the header. A 401/403
// (logged-out / no session) still resolves to EMPTY and never errors the shell.
const _viewerCache: { data: ViewerPortfolio | null; authenticated: boolean } = {
  data: null,
  authenticated: false,
};

export function useViewerPortfolio(): FetchState {
  // WO10 — use a tick counter to drive refetch on demand. Bumping the
  // counter causes the effect to re-run with the latest token.
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<FetchState>({
    data: _viewerCache.data ?? EMPTY,
    loading: _viewerCache.data ? false : true,
    error: null,
    authenticated: _viewerCache.authenticated,
  });

  useEffect(() => {
    let cancelled = false;
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(TOKEN_KEY)
        : null;

    if (!token) {
      setState({ data: EMPTY, loading: false, error: null, authenticated: false });
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const res = await fetch(apiV1Base() + '/user/portfolio', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          // Logged-out / no session — clear the cache and resolve to EMPTY.
          // Never surfaces as an error to the shell.
          _viewerCache.data = null;
          _viewerCache.authenticated = false;
          setState({
            data: EMPTY,
            loading: false,
            error: null,
            authenticated: false,
          });
          return;
        }
        if (!res.ok) {
          setState({
            data: EMPTY,
            loading: false,
            error: `HTTP ${res.status}`,
            authenticated: true,
          });
          return;
        }
        const body = await res.json();
        // Backend may wrap in {ok,data,...} envelope; unwrap defensively.
        const payload = (body && body.data) ? body.data : body;
        const merged: ViewerPortfolio = { ...EMPTY, ...(payload || {}) };
        _viewerCache.data = merged;          // FIX 5: remember last-good for SWR
        _viewerCache.authenticated = true;
        setState({ data: merged, loading: false, error: null, authenticated: true });
      } catch (err: any) {
        if (cancelled) return;
        // FIX 5: a transient network blip must not blank the header — keep the
        // last-good cached portfolio if we have one; only surface an error when
        // there is nothing cached to fall back to.
        if (_viewerCache.data) {
          setState({
            data: _viewerCache.data,
            loading: false,
            error: null,
            authenticated: _viewerCache.authenticated,
          });
          return;
        }
        setState({
          data: EMPTY,
          loading: false,
          error: err?.message || 'network error',
          authenticated: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { ...state, refetch: () => setTick(t => t + 1) };
}

export const VIEWER_PORTFOLIO_EMPTY = EMPTY;

export default useViewerPortfolio;
